import React, { useState, useEffect, useCallback } from 'react';
import instance from '../api/Axios'; // Use your configured axios instance

const UserPermissionAssignment = () => {
    const [users, setUsers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState('');
    const [bmoFilter, setBmoFilter] = useState('');
    const [bmos, setBmos] = useState([]);
    const [viewMode, setViewMode] = useState('templates'); // 'templates' or 'permissions'
    const [pendingPermissionChanges, setPendingPermissionChanges] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertData, setAlertData] = useState({ type: '', title: '', message: '' });

    const loadUsers = useCallback(async () => {
        try {
            const params = {};
            if (searchTerm) params.search = searchTerm;
            if (userTypeFilter) params.user_type = userTypeFilter;
            if (bmoFilter) params.bmo = bmoFilter;

            const response = await instance.get('/api/accounts/user-permissions/', { params });
            setUsers(response.data.results || response.data);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    }, [searchTerm, userTypeFilter, bmoFilter]);

    const loadTemplates = useCallback(async () => {
        try {
            const response = await instance.get('/api/accounts/permission-templates/');
            setTemplates(response.data.results || response.data);
        } catch (error) {
            console.error('Error loading templates:', error);
        }
    }, []);

    const loadGroupedPermissions = useCallback(async () => {
        try {
            const response = await instance.get('/api/accounts/permissions/grouped/');
            setGroupedPermissions(response.data);
        } catch (error) {
            console.error('Error loading grouped permissions:', error);
        }
    }, []);

    const loadBmos = useCallback(async () => {
        try {
            const response = await instance.get('/api/accounts/bmo/dropdown/');
            setBmos(response.data.results || response.data);
        } catch (error) {
            console.error('Error loading BMOs:', error);
            // If BMO endpoint doesn't exist, set empty array
            setBmos([]);
        }
    }, []);

    useEffect(() => {
        loadUsers();
        loadTemplates();
        loadBmos();
        if (viewMode === 'permissions') {
            loadGroupedPermissions();
        }
    }, [loadUsers, loadTemplates, loadGroupedPermissions, loadBmos, viewMode]);

    const loadUserPermissions = async (userId) => {
        setLoading(true);
        try {
            const response = await instance.get(`/api/accounts/user-permissions/${userId}/permissions_summary/`);
            setUserPermissions(response.data);
        } catch (error) {
            console.error('Error loading user permissions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        setUserPermissions(null);
        setPendingPermissionChanges([]);
        setHasUnsavedChanges(false);
        loadUserPermissions(user.id);
    };

    const handleTemplateToggle = async (templateId, isAssigned) => {
        if (!selectedUser) return;

        setLoading(true);
        try {
            const currentTemplateIds = selectedUser.permission_templates.map(t => t.id);
            const newTemplateIds = isAssigned 
                ? currentTemplateIds.filter(id => id !== templateId)
                : [...currentTemplateIds, templateId];

            await instance.patch(`/api/accounts/user-permissions/${selectedUser.id}/`, {
                template_ids: newTemplateIds
            });

            // Reload user data
            const updatedUser = users.map(u => 
                u.id === selectedUser.id 
                    ? { ...u, permission_templates: templates.filter(t => newTemplateIds.includes(t.id)) }
                    : u
            );
            setUsers(updatedUser);
            
            // Update selected user
            setSelectedUser({
                ...selectedUser,
                permission_templates: templates.filter(t => newTemplateIds.includes(t.id))
            });

            // Reload permissions summary
            loadUserPermissions(selectedUser.id);
        } catch (error) {
            console.error('Error updating user templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const showAlertModal = (type, title, message) => {
        setAlertData({ type, title, message });
        setShowAlert(true);
    };

    const closeAlert = () => {
        setShowAlert(false);
        setAlertData({ type: '', title: '', message: '' });
    };

    const applyTemplates = async () => {
        if (!selectedUser) return;

        console.log('Applying templates for user:', selectedUser.email);
        console.log('User templates:', selectedUser.permission_templates);

        setLoading(true);
        try {
            const response = await instance.post(`/api/accounts/user-permissions/${selectedUser.id}/apply_templates/`, {
                overwrite: true
            });
            
            console.log('Apply templates response:', response.data);
            
            // Show success message
            showAlertModal(
                'success', 
                'Templates Applied Successfully!', 
                `Templates applied to ${selectedUser.email}\n\nTemplates applied: ${response.data.templates.join(', ')}\nTotal permissions: ${response.data.total_permissions}`
            );
            
            // Reload user permissions to see the changes
            await loadUserPermissions(selectedUser.id);
            
            // Also reload the user list to get updated template assignments
            await loadUsers();
            
            console.log('Templates applied successfully');
        } catch (error) {
            console.error('Error applying templates:', error);
            console.error('Error response:', error.response?.data);
            showAlertModal(
                'error', 
                'Error Applying Templates', 
                `Error applying templates: ${error.response?.data?.message || error.message}`
            );
        } finally {
            setLoading(false);
        }
    };

    const handleIndividualPermissionChange = (permissionId, isChecked) => {
        if (!selectedUser) return;
        
        // Track the change locally instead of immediately saving
        let updatedPermissions = [...(userPermissions?.direct_permissions || [])];
        
        if (isChecked) {
            // Add permission if not already present
            if (!updatedPermissions.find(p => p.id === permissionId)) {
                // Find the permission object from grouped permissions
                const permission = groupedPermissions
                    .flatMap(group => group.permissions)
                    .find(p => p.id === permissionId);
                
                if (permission) {
                    updatedPermissions.push(permission);
                }
            }
        } else {
            // Remove permission
            updatedPermissions = updatedPermissions.filter(p => p.id !== permissionId);
        }
        
        // Update local state
        setUserPermissions(prev => ({
            ...prev,
            direct_permissions: updatedPermissions
        }));
        
        // Track changes for saving
        setPendingPermissionChanges(updatedPermissions.map(p => p.id));
        setHasUnsavedChanges(true);
    };

    const saveIndividualPermissions = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await instance.patch(`/api/accounts/user-permissions/${selectedUser.id}/`, {
                user_permissions: pendingPermissionChanges
            });
            
            // Reload user permissions to get the latest state
            await loadUserPermissions(selectedUser.id);
            
            // Clear pending changes
            setPendingPermissionChanges([]);
            setHasUnsavedChanges(false);
            
            console.log('Permissions saved successfully');
        } catch (error) {
            console.error('Error saving individual permissions:', error);
            // Reload to restore the correct state
            await loadUserPermissions(selectedUser.id);
        } finally {
            setLoading(false);
        }
    };

    const isPermissionChecked = (permissionId) => {
        if (!userPermissions) return false;
        
        // Check if permission is in direct permissions, template permissions, or group permissions
        const allPermissions = [
            ...(userPermissions.direct_permissions || []),
            ...(userPermissions.template_permissions || []),
            ...(userPermissions.group_permissions || [])
        ];
        
        // First check if the permission is currently held
        const hasPermission = allPermissions.some(perm => perm.id === permissionId);
        
        // Check if there are pending changes for this permission
        if (hasUnsavedChanges && pendingPermissionChanges) {
            // If permission is in pending changes, use that state
            return Boolean(pendingPermissionChanges.includes(permissionId));
        }
        
        return Boolean(hasPermission);
    };

    const isPermissionFromTemplate = (permissionId) => {
        if (!userPermissions) return false;
        return userPermissions.template_permissions?.some(perm => perm.id === permissionId) || false;
    };

    const userTypes = [
        { value: '', label: 'All User Types' },
        { value: 'primary_representative', label: 'Primary Representative' },
        { value: 'secondary_representative', label: 'Secondary Representative' },
        { value: 'bmo_officer', label: 'BMO Officer' },
        { value: 'funder_primary_representative', label: 'Funder Primary Representative' },
        { value: 'funder_secondary_representative', label: 'Funder Secondary Representative' },
        { value: 'funder_officer', label: 'Funder Officer' },
    ];

    return (
        <div className="user-permission-assignment">
            <div className="header">
                <h2>User Permission Assignment</h2>
                <div className="view-toggle">
                    <button 
                        className={viewMode === 'templates' ? 'active' : ''} 
                        onClick={() => setViewMode('templates')}
                    >
                        Permission Templates
                    </button>
                    <button 
                        className={viewMode === 'permissions' ? 'active' : ''} 
                        onClick={() => setViewMode('permissions')}
                    >
                        Individual Permissions
                    </button>
                </div>
            </div>

            <div className="content">
                {/* User List */}
                <div className="user-list-section">
                    <div className="filters">
                        <input
                            type="text"
                            placeholder="Search users by email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            value={userTypeFilter}
                            onChange={(e) => setUserTypeFilter(e.target.value)}
                        >
                            {userTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        <select
                            value={bmoFilter}
                            onChange={(e) => setBmoFilter(e.target.value)}
                        >
                            <option value="">All BMOs</option>
                            {bmos.map(bmo => (
                                <option key={bmo.id} value={bmo.id}>
                                    {bmo.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="user-list">
                        <h3>Users</h3>
                        {users.map(user => (
                            <div 
                                key={user.id} 
                                className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                                onClick={() => handleUserSelect(user)}
                            >
                                <div className="user-info">
                                    <strong>{user.email}</strong>
                                    <span className="user-type">{user.user_type.replace('_', ' ')}</span>
                                </div>
                                <div className="template-count">
                                    {user.permission_templates.length} groups
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Permission Assignment */}
                {selectedUser && (
                    <div className="permission-assignment">
                        <div className="user-header">
                            <div>
                                <p>Permissions for {selectedUser.email}</p>
                                <span className="user-type">{selectedUser.user_type.replace('_', ' ')}</span>
                            </div>
                        </div>
                        {viewMode === 'templates' && (
                            <button 
                                onClick={applyTemplates}
                                disabled={loading || !selectedUser?.permission_templates?.length}
                                title={!selectedUser?.permission_templates?.length ? 'No templates assigned to this user' : 'Apply selected templates to user permissions'}
                            >
                                {loading ? 'Applying...' : 
                                 !selectedUser?.permission_templates?.length ? 'No Templates to Apply' :
                                 `Apply ${selectedUser.permission_templates.length} Template(s)`}
                            </button>
                        )}

                        {/* Templates View */}
                        {viewMode === 'templates' && (
                            <div className="template-assignment">
                                <h4>Permission Templates</h4>
                                <div className="templates-grid">
                                    {templates.map(template => {
                                        const isAssigned = selectedUser.permission_templates.some(t => t.id === template.id);
                                        return (
                                            <div key={template.id} className="template-item">
                                                <label className="template-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={isAssigned}
                                                        onChange={() => handleTemplateToggle(template.id, isAssigned)}
                                                        disabled={loading}
                                                    />
                                                    <div className="template-info">
                                                        <strong>{template.name}</strong>
                                                        <p>{template.description}</p>
                                                        <span className="permission-count">
                                                            {template.permission_count} permissions
                                                        </span>
                                                    </div>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Individual Permissions View */}
                        {viewMode === 'permissions' && (
                            <div className="permissions-section">
                                <div className="permissions-header">
                                    <h4>Individual Permissions</h4>
                                    {hasUnsavedChanges && (
                                        <button 
                                            className="save-button"
                                            onClick={saveIndividualPermissions}
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    )}
                                </div>
                                <div className="permissions-grid">
                                    {/* First, collect all representative permissions from all groups */}
                                    {(() => {
                                        const allRepresentativePermissions = [];
                                        groupedPermissions.forEach(group => {
                                            const functionalPermissions = group.permissions.filter(permission => {
                                                const codename = permission.codename || permission.name?.toLowerCase();
                                                
                                                // List of actually implemented permissions
                                                const implementedPermissions = [
                                                    // User management
                                                    'view_user', 'add_user', 'change_user', 'delete_user',
                                                    // Funder management
                                                    'view_funder', 'add_funder', 'change_funder', 'delete_funder',
                                                    // Bursary management
                                                    'view_bursary', 'add_bursary', 'change_bursary', 'delete_bursary',
                                                    // Funder officers
                                                    'view_funderofficer', 'add_funderofficer', 'change_funderofficer', 'delete_funderofficer',
                                                    // Funder representatives (primary and secondary - will be unified)
                                                    'view_funderprimaryrepresentative', 'add_funderprimaryrepresentative', 'change_funderprimaryrepresentative', 'delete_funderprimaryrepresentative',
                                                    'view_fundersecondaryrepresentative', 'add_fundersecondaryrepresentative', 'change_fundersecondaryrepresentative', 'delete_fundersecondaryrepresentative',
                                                    // Funder applications
                                                    'view_funderapplication', 'add_funderapplication', 'change_funderapplication', 'delete_funderapplication',
                                                    // Funder agreements
                                                    'view_funderagreement', 'add_funderagreement', 'change_funderagreement', 'delete_funderagreement',
                                                    // Funder payments
                                                    'view_funderpayment', 'add_funderpayment', 'change_funderpayment', 'delete_funderpayment',
                                                    // Permission management
                                                    'view_permissiontemplate', 'add_permissiontemplate', 'change_permissiontemplate', 'delete_permissiontemplate',
                                                    // BMO management (basic)
                                                    'view_bmo', 'add_bmo', 'change_bmo',
                                                    // Student management (basic)
                                                    'view_student', 'add_student', 'change_student'
                                                ];
                                                
                                                return implementedPermissions.includes(codename);
                                            });
                                            
                                            const representativePermissions = functionalPermissions.filter(permission => {
                                                const codename = permission.codename || permission.name?.toLowerCase();
                                                return codename.includes('representative');
                                            });
                                            
                                            allRepresentativePermissions.push(...representativePermissions);
                                        });
                                        
                                        // Render unified representatives section once if any representative permissions exist
                                        return allRepresentativePermissions.length > 0 ? (
                                            <div className="unified-permission-section">
                                                <h6 className="unified-section-title">
                                                    <i className="fas fa-users me-2"></i>
                                                    Funder Representatives (Unified CRUD)
                                                </h6>
                                                
                                                {/* CRUD Table */}
                                                <div className="crud-table">
                                                    <div className="crud-table-header">
                                                        <div className="crud-header-cell">Permission Type</div>
                                                        <div className="crud-header-cell">
                                                            <i className="fas fa-eye me-1"></i>
                                                            View
                                                        </div>
                                                        <div className="crud-header-cell">
                                                            <i className="fas fa-plus me-1"></i>
                                                            Add
                                                        </div>
                                                        <div className="crud-header-cell">
                                                            <i className="fas fa-edit me-1"></i>
                                                            Change
                                                        </div>
                                                        <div className="crud-header-cell">
                                                            <i className="fas fa-trash me-1"></i>
                                                            Delete
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="crud-table-row">
                                                        <div className="crud-row-label">
                                                            <i className="fas fa-users me-2"></i>
                                                            <strong>Representatives</strong>
                                                            <small>Primary & Secondary</small>
                                                        </div>
                                                        
                                                        {['view', 'add', 'change', 'delete'].map(action => {
                                                            // Find both primary and secondary permissions for this action
                                                            const primaryPerm = allRepresentativePermissions.find(p => 
                                                                (p.codename || p.name?.toLowerCase()) === `${action}_funderprimaryrepresentative`
                                                            );
                                                            const secondaryPerm = allRepresentativePermissions.find(p => 
                                                                (p.codename || p.name?.toLowerCase()) === `${action}_fundersecondaryrepresentative`
                                                            );
                                                            
                                                            // Check if either primary or secondary is checked
                                                            const primaryChecked = primaryPerm ? isPermissionChecked(primaryPerm.id) : false;
                                                            const secondaryChecked = secondaryPerm ? isPermissionChecked(secondaryPerm.id) : false;
                                                            const isChecked = Boolean(primaryChecked || secondaryChecked);
                                                            
                                                            // Check if either is from template
                                                            const primaryFromTemplate = primaryPerm && isPermissionFromTemplate(primaryPerm.id);
                                                            const secondaryFromTemplate = secondaryPerm && isPermissionFromTemplate(secondaryPerm.id);
                                                            const isFromTemplate = primaryFromTemplate || secondaryFromTemplate;
                                                            
                                                            return (
                                                                <div key={action} className={`crud-checkbox-cell ${isFromTemplate ? 'from-template' : ''}`}>
                                                                    <label className="crud-checkbox">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isChecked}
                                                                            disabled={isFromTemplate || loading}
                                                                            onChange={(e) => {
                                                                                // Toggle both primary and secondary permissions together
                                                                                const newState = e.target.checked;
                                                                                if (primaryPerm) {
                                                                                    handleIndividualPermissionChange(primaryPerm.id, newState);
                                                                                }
                                                                                if (secondaryPerm) {
                                                                                    handleIndividualPermissionChange(secondaryPerm.id, newState);
                                                                                }
                                                                            }}
                                                                        />
                                                                    </label>
                                                                    {isFromTemplate && (
                                                                        <div className="template-indicator">
                                                                            <i className="fas fa-crown" title="From Template"></i>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                
                                                <div className="crud-info">
                                                    <small className="text-muted">
                                                        <i className="fas fa-info-circle me-1"></i>
                                                        Each checkbox controls both Primary and Secondary representative permissions
                                                    </small>
                                                </div>
                                            </div>
                                        ) : null;
                                    })()}
                                    
                                    {/* Regular permission groups (excluding representatives) */}
                                    {groupedPermissions.map(group => {
                                        // Filter out dummy permissions - only show implemented ones
                                        const functionalPermissions = group.permissions.filter(permission => {
                                            const codename = permission.codename || permission.name?.toLowerCase();
                                            
                                            // List of actually implemented permissions
                                            const implementedPermissions = [
                                                // User management
                                                'view_user', 'add_user', 'change_user', 'delete_user',
                                                // Funder management
                                                'view_funder', 'add_funder', 'change_funder', 'delete_funder',
                                                // Bursary management
                                                'view_bursary', 'add_bursary', 'change_bursary', 'delete_bursary',
                                                // Funder officers
                                                'view_funderofficer', 'add_funderofficer', 'change_funderofficer', 'delete_funderofficer',
                                                // Funder representatives (primary and secondary - will be unified)
                                                'view_funderprimaryrepresentative', 'add_funderprimaryrepresentative', 'change_funderprimaryrepresentative', 'delete_funderprimaryrepresentative',
                                                'view_fundersecondaryrepresentative', 'add_fundersecondaryrepresentative', 'change_fundersecondaryrepresentative', 'delete_fundersecondaryrepresentative',
                                                // Funder applications
                                                'view_funderapplication', 'add_funderapplication', 'change_funderapplication', 'delete_funderapplication',
                                                // Funder agreements
                                                'view_funderagreement', 'add_funderagreement', 'change_funderagreement', 'delete_funderagreement',
                                                // Funder payments
                                                'view_funderpayment', 'add_funderpayment', 'change_funderpayment', 'delete_funderpayment',
                                                // Permission management
                                                'view_permissiontemplate', 'add_permissiontemplate', 'change_permissiontemplate', 'delete_permissiontemplate',
                                                // BMO management (basic)
                                                'view_bmo', 'add_bmo', 'change_bmo',
                                                // Student management (basic)
                                                'view_student', 'add_student', 'change_student'
                                            ];
                                            
                                            return implementedPermissions.includes(codename);
                                        });
                                        
                                        // Only show non-representative permissions in regular groups
                                        const otherPermissions = functionalPermissions.filter(permission => {
                                            const codename = permission.codename || permission.name?.toLowerCase();
                                            return !codename.includes('representative');
                                        });
                                        
                                        // Only show groups that have non-representative permissions
                                        if (otherPermissions.length === 0) {
                                            return null;
                                        }
                                        
                                        return (
                                            <div key={`${group.app_label}.${group.model_name}`} className="permission-group">
                                                <h5>{group.model_verbose_name} ({group.app_label}) - {otherPermissions.length} functional</h5>
                                                <div className="permission-items">
                                                    {/* Regular permissions (excluding representatives) */}
                                                    {otherPermissions.map(permission => {
                                                        const isChecked = isPermissionChecked(permission.id);
                                                        const isFromTemplate = isPermissionFromTemplate(permission.id);
                                                        return (
                                                            <div key={permission.id} className={`permission-item ${isFromTemplate ? 'from-template' : ''}`}>
                                                                <label className="permission-checkbox">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => handleIndividualPermissionChange(permission.id, e.target.checked)}
                                                                        disabled={isFromTemplate || loading}
                                                                    />
                                                                    <span className="permission-name">
                                                                        {permission.name}
                                                                        {isFromTemplate && <span className="template-badge">From Template</span>}
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    }).filter(Boolean)}
                                    
                                    {/* Show info about filtered permissions */}
                                    <div className="filtered-info">
                                        <p className="text-muted small">
                                            <i className="fas fa-info-circle me-2"></i>
                                            Only showing functional permissions that actually control UI elements. 
                                            Dummy/unused permissions are hidden.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Permissions Summary */}
                        {userPermissions && (
                            <div className="permissions-summary">
                                <h4>Permissions Summary</h4>
                                
                                <div className="permission-sections">
                                    <div className="permission-section">
                                        <h5>From Templates ({userPermissions.template_permissions.length})</h5>
                                        <div className="permission-list">
                                            {userPermissions.template_permissions.map(perm => (
                                                <span key={perm.id} className="permission-badge template">
                                                    {perm.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="permission-section">
                                        <h5>Direct Permissions ({userPermissions.direct_permissions.length})</h5>
                                        <div className="permission-list">
                                            {userPermissions.direct_permissions.map(perm => (
                                                <span key={perm.id} className="permission-badge direct">
                                                    {perm.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="permission-section">
                                        <h5>From Groups ({userPermissions.group_permissions.length})</h5>
                                        <div className="permission-list">
                                            {userPermissions.group_permissions.map(perm => (
                                                <span key={perm.id} className="permission-badge group">
                                                    {perm.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <style>{`
                .user-permission-assignment {
                    padding: 20px;
                    max-width: 1400px;
                    margin: 0 auto;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .view-toggle {
                    display: flex;
                    gap: 10px;
                }

                .view-toggle button {
                    padding: 8px 16px;
                    border: 1px solid #ddd;
                    background: white;
                    color: #333;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-weight: 500;
                }

                .view-toggle button.active {
                    background: #4472C4;
                    color: white;
                    border-color: #4472C4;
                }

                .view-toggle button:hover:not(.active) {
                    background: #f5f5f5;
                    color: #000;
                    border-color: #999;
                }

                .content {
                    display: grid;
                    grid-template-columns: 1fr 2fr;
                    gap: 30px;
                    margin-top: 20px;
                }

                .filters {
                    display: flex;
                    gap: 15px;
                    margin-bottom: 20px;
                }

                .filters input,
                .filters select {
                    padding: 8px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    flex: 1;
                }

                .user-list {
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    background: white;
                    max-height: 600px;
                    overflow-y: auto;
                }

                .user-list h3 {
                    margin: 0;
                    padding: 15px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #ddd;
                }

                .user-item {
                    padding: 15px;
                    border-bottom: 1px solid #eee;
                    cursor: pointer;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .user-item:hover {
                    background: #f5f5f5;
                }

                .user-item.selected {
                    background: #e3f2fd;
                    border-left: 4px solid #4472C4;
                }

                .user-type {
                    display: block;
                    font-size: 12px;
                    color: #666;
                    text-transform: capitalize;
                }

                .template-count {
                    font-size: 12px;
                    color: #666;
                    background: #f0f0f0;
                    padding: 2px 6px;
                    border-radius: 10px;
                }

                .permission-assignment {
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    background: white;
                    padding: 20px;
                    max-height: 800px;
                    overflow-y: auto;
                }

                .user-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    padding-bottom: 15px;
                    border-bottom: 1px solid #eee;
                }

                .templates-grid {
                    display: grid;
                    gap: 15px;
                    margin-bottom: 30px;
                }

                .template-item {
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    padding: 15px;
                }

                .template-checkbox {
                    display: flex;
                    gap: 12px;
                    cursor: pointer;
                    align-items: flex-start;
                }

                .template-info strong {
                    display: block;
                    margin-bottom: 5px;
                    color: #333;
                }

                .template-info p {
                    margin: 0 0 8px 0;
                    font-size: 14px;
                    color: #666;
                }

                .permission-count {
                    font-size: 12px;
                    color: #4472C4;
                    font-weight: bold;
                }

                /* Individual Permissions Styles */
                .permissions-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #ddd;
                }

                .permissions-header h4 {
                    margin: 0;
                    color: #333;
                }

                .save-button {
                    background: #28a745 !important;
                    color: white;
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .save-button:hover:not(:disabled) {
                    background: #218838 !important;
                }

                .save-button:disabled {
                    background: #6c757d !important;
                    cursor: not-allowed;
                }

                .permissions-grid {
                    display: grid;
                    gap: 20px;
                }

                .permission-group {
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    padding: 15px;
                    background: #fafafa;
                }

                .permission-group h5 {
                    margin: 0 0 15px 0;
                    color: #333;
                    font-size: 16px;
                    border-bottom: 1px solid #ddd;
                    padding-bottom: 8px;
                }

                .permission-items {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 10px;
                }

                .permission-item {
                    background: white;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    padding: 10px;
                }

                .permission-item.from-template {
                    background: #e3f2fd;
                    border-color: #90caf9;
                }

                .permission-checkbox {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    font-size: 14px;
                }

                .permission-checkbox input[type="checkbox"] {
                    margin: 0;
                }

                .permission-name {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .template-badge {
                    background: #1976d2;
                    color: white;
                    font-size: 10px;
                    padding: 2px 6px;
                    border-radius: 10px;
                    font-weight: bold;
                }

                .permission-sections {
                    display: grid;
                    gap: 20px;
                }

                .permission-section h5 {
                    margin: 0 0 10px 0;
                    color: #333;
                }

                .permission-list {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 6px;
                }

                .permission-badge {
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .permission-badge.template {
                    background: #e3f2fd;
                    color: #1976d2;
                }

                .permission-badge.direct {
                    background: #f3e5f5;
                    color: #7b1fa2;
                }

                .permission-badge.group {
                    background: #e8f5e8;
                    color: #388e3c;
                }

                button {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 4px;
                    background: #4472C4;
                    color: white;
                    cursor: pointer;
                    font-size: 14px;
                }

                button:hover {
                    background: #365a96;
                }

                button:disabled {
                    background: #ccc;
                    cursor: not-allowed;
                }

                .filtered-info {
                    margin-top: 20px;
                    padding: 15px;
                    background: #f8f9fa;
                    border-radius: 6px;
                    border-left: 4px solid #17a2b8;
                    grid-column: 1 / -1;
                }

                .filtered-info p {
                    margin: 0;
                    color: #666;
                    font-size: 13px;
                    line-height: 1.4;
                }

                .filtered-info .fas {
                    color: #17a2b8;
                }

                .unified-permission-section {
                    grid-column: 1 / -1;
                    margin: 15px 0;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 8px;
                    border-left: 4px solid #007bff;
                }

                .unified-section-title {
                    margin: 0 0 15px 0;
                    color: #007bff;
                    font-size: 16px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                }

                .unified-crud-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 15px;
                }

                .crud-table {
                    background: white;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    border: 1px solid #e9ecef;
                    margin-bottom: 15px;
                }

                .crud-table-header {
                    display: grid;
                    grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
                    background: linear-gradient(135deg, #007bff, #0056b3);
                    color: white;
                    font-weight: 600;
                    font-size: 14px;
                }

                .crud-header-cell {
                    padding: 15px 12px;
                    text-align: center;
                    border-right: 1px solid rgba(255,255,255,0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;
                }

                .crud-header-cell:first-child {
                    text-align: left;
                    justify-content: flex-start;
                    background: rgba(0,0,0,0.1);
                }

                .crud-header-cell:last-child {
                    border-right: none;
                }

                .crud-table-row {
                    display: grid;
                    grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
                    border-bottom: 1px solid #e9ecef;
                    transition: background-color 0.2s ease;
                }

                .crud-table-row:hover {
                    background-color: #f8f9fa;
                }

                .crud-row-label {
                    padding: 20px 15px;
                    background: #f8f9fa;
                    border-right: 1px solid #e9ecef;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    gap: 4px;
                }

                .crud-row-label strong {
                    color: #2c3e50;
                    font-size: 16px;
                    font-weight: 600;
                }

                .crud-row-label small {
                    color: #6c757d;
                    font-size: 12px;
                    font-style: italic;
                }

                .crud-checkbox-cell {
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-right: 1px solid #e9ecef;
                    background: white;
                    position: relative;
                }

                .crud-checkbox-cell:last-child {
                    border-right: none;
                }

                .crud-checkbox-cell.from-template {
                    background: #fff3cd;
                }

                .crud-checkbox {
                    position: relative;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .crud-checkbox input[type="checkbox"] {
                    width: 24px;
                    height: 24px;
                    margin: 0;
                    cursor: pointer;
                    accent-color: #007bff;
                    transform: scale(1.2);
                }

                .crud-checkbox input[type="checkbox"]:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .crud-checkbox input[type="checkbox"]:checked {
                    animation: checkboxPulse 0.3s ease;
                }

                @keyframes checkboxPulse {
                    0% { transform: scale(1.2); }
                    50% { transform: scale(1.4); }
                    100% { transform: scale(1.2); }
                }

                .template-indicator {
                    position: absolute;
                    top: 5px;
                    right: 5px;
                    color: #ffc107;
                    font-size: 12px;
                }

                .crud-info {
                    margin-top: 12px;
                    padding: 10px 15px;
                    background: #e3f2fd;
                    border-radius: 6px;
                    border-left: 4px solid #2196f3;
                }

                .crud-info small {
                    color: #1565c0;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                /* Responsive Design for CRUD Table */
                @media (max-width: 768px) {
                    .crud-table-header,
                    .crud-table-row {
                        grid-template-columns: 1fr;
                    }
                    
                    .crud-header-cell:not(:first-child) {
                        display: none;
                    }
                    
                    .crud-row-label {
                        border-right: none;
                        border-bottom: 1px solid #e9ecef;
                        text-align: center;
                    }
                    
                    .crud-checkbox-cell {
                        display: none;
                    }
                    
                    .crud-table-row::after {
                        content: "📱 Mobile view: Use desktop for full CRUD controls";
                        grid-column: 1;
                        padding: 15px;
                        text-align: center;
                        color: #6c757d;
                        font-style: italic;
                        background: #f8f9fa;
                        font-size: 12px;
                    }
                }

                .unified-permission-item {
                    background: white;
                    border-radius: 6px;
                    border: 2px solid #e9ecef;
                    overflow: hidden;
                    transition: all 0.2s ease;
                }

                .unified-permission-item:hover {
                    border-color: #007bff;
                    box-shadow: 0 2px 8px rgba(0,123,255,0.1);
                }

                .unified-permission-item.from-template {
                    background: #fff3cd;
                    border-color: #ffc107;
                }

                .unified-permission-checkbox {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 12px;
                    cursor: pointer;
                    width: 100%;
                    height: 100%;
                }

                .unified-permission-checkbox input[type="checkbox"] {
                    margin: 0;
                    width: 18px;
                    height: 18px;
                    accent-color: #007bff;
                }

                .unified-permission-name {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                    flex: 1;
                    font-size: 14px;
                    font-weight: 600;
                    color: #2c3e50;
                }

                .unified-permission-name small {
                    font-size: 12px;
                    font-weight: normal;
                    color: #6c757d;
                    font-style: italic;
                }

                .unified-permission-name .template-badge {
                    background: #ffc107;
                    color: #856404;
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 10px;
                    font-weight: 500;
                    margin-top: 2px;
                    align-self: flex-start;
                }

                @media (max-width: 768px) {
                    .content {
                        grid-template-columns: 1fr;
                    }
                    
                    .permission-items {
                        grid-template-columns: 1fr;
                    }
                }

                /* Alert Modal Styles */
                .alert-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }

                .alert-modal {
                    background: white;
                    border-radius: 8px;
                    min-width: 400px;
                    max-width: 600px;
                    max-height: 80vh;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                }

                .alert-header {
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-bottom: 1px solid #eee;
                }

                .alert-header.success {
                    background: #d4edda;
                    color: #155724;
                }

                .alert-header.error {
                    background: #f8d7da;
                    color: #721c24;
                }

                .alert-header h3 {
                    margin: 0;
                    font-size: 18px;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    color: #999;
                    padding: 0;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-btn:hover {
                    color: #666;
                }

                .alert-body {
                    padding: 20px;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .alert-body pre {
                    white-space: pre-wrap;
                    word-wrap: break-word;
                    margin: 0;
                    font-family: inherit;
                    font-size: 14px;
                    line-height: 1.5;
                }

                .alert-footer {
                    padding: 16px 20px;
                    border-top: 1px solid #eee;
                    display: flex;
                    justify-content: flex-end;
                }

                .ok-btn {
                    background: #007bff;
                    color: white;
                    border: none;
                    padding: 8px 20px;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 14px;
                }

                .ok-btn:hover {
                    background: #0056b3;
                }
            `}</style>

            {/* Alert Modal */}
            {showAlert && (
                <div className="alert-modal-overlay" onClick={closeAlert}>
                    <div className="alert-modal" onClick={(e) => e.stopPropagation()}>
                        <div className={`alert-header ${alertData.type}`}>
                            <h3>{alertData.title}</h3>
                            <button className="close-btn" onClick={closeAlert}>×</button>
                        </div>
                        <div className="alert-body">
                            <pre>{alertData.message}</pre>
                        </div>
                        <div className="alert-footer">
                            <button onClick={closeAlert} className="ok-btn">OK</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserPermissionAssignment;
